/**
 * UI chrome dictionary — strings shared across components and pages.
 * Page-specific prose lives in per-page `copy` objects, not here.
 *
 * The machine voice (mono) mostly stays in a compact, code-like register in
 * both languages; the human voice (sans) is fully translated.
 */

import type { Lang } from './config';
import type { Freshness } from '../lab/types';

export interface Dict {
  // navigation
  nav: {
    command: string;
    anu: string;
    research: string;
    projects: string;
    agents: string;
    knowledge: string;
    publications: string;
    observatory: string;
    about: string;
    contact: string;
  };
  // header
  header: {
    nodeOnline: string;
    ask: string;
    askAria: string;
    audioOnAria: string;
    audioOffAria: string;
    openMenu: string;
    close: string;
    menuAria: string;
    groupLab: string;
    groupIndex: string;
    homeAria: string;
    skip: string;
    langSwitchAria: string;
  };
  // footer
  footer: {
    tagline: string;
    /** Freshness-keyed: which one is shown depends on the live bridge's current state (`displayFreshness`). */
    liveNote: Record<Freshness, string>;
    /** Appended to `liveNote` when the observed run is an engineering canary. */
    canaryNote: string;
    sections: string;
    elsewhere: string;
    footerAria: string;
    rights: string;
  };
  // consent
  consent: {
    text: string;
    privacyLink: string;
    essential: string;
    allow: string;
  };
  // proactive hints
  hint: {
    eyebrow: string;
    dismiss: string;
    knowledge: { text: string; action: string };
    knowledgeResearch: { text: string; action: string };
    applied: { text: string; action: string };
    publications: { text: string; action: string };
  };
  // adaptive spotlight
  spotlight: {
    researcher: string;
    developer: string;
    partner: string;
    dismiss: string;
  };
  // ask lab
  ask: {
    interface: string;
    palette: string;
    terminal: string;
    esc: string;
    placeholder: string;
    thinking: string;
    stop: string;
    select: string;
    run: string;
    escClose: string;
    footnotePrefix: string; // before the "How this works" link
    footnoteLink: string;
    quick: {
      research: string;
      projects: string;
      agents: string;
      knowledge: string;
      observatory: string;
      publications: string;
      compare: string;
      researching: string;
      graph: string;
      terminal: string;
    };
    hint: {
      research: string;
      projects: string;
      agents: string;
      knowledge: string;
      observatory: string;
      publications: string;
      compare: string;
      askLab: string;
      graph: string;
      mode: string;
    };
    msg: {
      offline: string;
      rate: string;
      generic: string;
      tooLong: string;
    };
    askThingLabel: string; // hero CTA "Ask the laboratory"
  };
  // live activity feed
  live: {
    label: string;
    awaiting: string;
    expandAria: string;
    collapseAria: string;
    real: string;
    /** Panel note prefix shown only while OFFLINE (no live roster has ever landed). */
    simulationLabel: string;
    /** Panel note suffix, paired with `real` — "… · REAL EVENTS MARKED ·real". */
    realEventsMarked: string;
    /** Panel note prefix while the source is still unknown (SYNCING). */
    unknownLabel: string;
    /** Marker for an observer-backed run that is not the flagship universe. */
    canaryLabel: string;
    /** Shown when the browser refused a frame the bridge sent — never swallowed. */
    faultLabel: string;
    faultAria: string;
    faultRosterRejected: string;
    faultStatusRejected: string;
  };
  // compare overlay
  compare: {
    title: string;
    closeAria: string;
    status: string;
    domains: string;
    relatedResearch: string;
  };
  // common labels reused on pages
  common: {
    status: string;
    domains: string;
    relatedResearch: string;
    projects: string;
    knowledgeConnections: string;
    allProjects: string;
    allResearch: string;
    links: string;
    summary: string;
    hypothesis: string;
    problem: string;
    currentState: string;
    methods: string;
    findings: string;
    state: string;
    zone: string;
  };
}

export const dictionaries: Record<Lang, Dict> = {
  en: {
    nav: {
      command: 'Command',
      anu: 'ANU Live',
      research: 'Research',
      projects: 'Projects',
      agents: 'Agents',
      knowledge: 'Knowledge',
      publications: 'Publications',
      observatory: 'Observatory',
      about: 'About',
      contact: 'Contact',
    },
    header: {
      nodeOnline: 'NODE ONLINE',
      ask: 'ASK LAB',
      askAria: 'Ask the laboratory (opens command palette)',
      audioOnAria: 'Turn ambient audio off',
      audioOffAria: 'Turn ambient audio on',
      openMenu: 'Open menu',
      close: 'CLOSE',
      menuAria: 'Site menu',
      groupLab: 'Laboratory',
      groupIndex: 'Index',
      homeAria: 'home',
      skip: 'Skip to content',
      langSwitchAria: 'Switch language',
    },
    footer: {
      tagline: 'Independent AI-first R&D Laboratory',
      liveNote: {
        LIVE: 'Ambient agent activity is the real NanoAgent population, chain-verified by this site.',
        STALE: 'Ambient agent activity reflects the last chain-verified population; the live universe is currently stale.',
        SYNCING:
          'This site has not established yet where its ambient agent layer comes from — it is still connecting to the observer.',
        OFFLINE: 'Ambient agent telemetry on this site is a labeled simulation.',
      },
      canaryNote:
        'The observed run is an engineering canary, not the flagship universe — some of its decisions may be the engine’s code fallback rather than a model.',
      sections: 'Sections',
      elsewhere: 'Elsewhere',
      footerAria: 'Footer',
      rights: 'Independent AI-first R&D Laboratory',
    },
    consent: {
      text: 'This site runs its behavioral layer locally in your browser. Optional analytics load only with your consent.',
      privacyLink: 'Privacy policy',
      essential: 'Essential only',
      allow: 'Allow analytics',
    },
    hint: {
      eyebrow: 'LAB · observation',
      dismiss: 'Dismiss',
      knowledge: {
        text: 'You seem interested in how knowledge moves through the laboratory.',
        action: 'Explore the Knowledge System →',
      },
      knowledgeResearch: {
        text: 'You seem interested in how knowledge moves through the laboratory.',
        action: 'See the research behind it →',
      },
      applied: {
        text: 'Looking for what the laboratory can build?',
        action: 'Explore applied research →',
      },
      publications: {
        text: 'The laboratory keeps its claims checkable.',
        action: 'See publications & artifacts →',
      },
    },
    spotlight: {
      researcher:
        'Signal detected: research interest · prioritized: Research, Publications',
      developer:
        'Signal detected: engineering interest · prioritized: Agents, Knowledge, GitHub',
      partner:
        'Signal detected: partnership interest · prioritized: Projects, Contact',
      dismiss: 'Dismiss',
    },
    ask: {
      interface: 'ASK LAB · interface layer',
      palette: 'palette',
      terminal: 'terminal',
      esc: 'esc',
      placeholder: 'Ask the laboratory, or pick a command…',
      thinking: 'LAB is thinking …',
      stop: 'esc to stop',
      select: 'select',
      run: 'run',
      escClose: 'esc close',
      footnotePrefix:
        'Questions leave your browser only via the lab server, to a managed model service. No profiling, no long-term history. ',
      footnoteLink: 'How this works',
      quick: {
        research: 'Research',
        projects: 'Projects',
        agents: 'Agents',
        knowledge: 'Knowledge',
        observatory: 'Observatory',
        publications: 'Publications',
        compare: 'Compare StudyNinja and KnowledgeBase AI',
        researching: 'What are you researching?',
        graph: 'Show the knowledge graph',
        terminal: 'terminal',
      },
      hint: {
        research: '/research',
        projects: '/projects',
        agents: '/agents',
        knowledge: '/knowledge',
        observatory: '/observatory',
        publications: '/publications',
        compare: 'compare',
        askLab: 'ask LAB',
        graph: 'graph',
        mode: 'mode',
      },
      msg: {
        offline:
          "The AI gateway is offline right now. The terminal still answers from the registries — type 'terminal'.",
        rate: 'Rate limit reached. The gateway accepts a few questions per minute — try again shortly.',
        generic: 'The gateway did not respond. Try again in a moment.',
        tooLong:
          'That question is too long — keep it under 2000 characters and ask again.',
      },
      askThingLabel: 'Ask the laboratory',
    },
    live: {
      label: 'LAB FEED',
      awaiting: 'AWAITING TELEMETRY —',
      expandAria: 'Expand live laboratory feed',
      collapseAria: 'Collapse live laboratory feed',
      real: 'real',
      simulationLabel: 'AMBIENT SIMULATION',
      realEventsMarked: 'REAL EVENTS MARKED',
      unknownLabel: 'SOURCE NOT YET ESTABLISHED',
      canaryLabel: 'ENGINEERING CANARY',
      faultLabel: 'FRAME REFUSED',
      faultAria: 'The browser refused a frame from the laboratory bridge',
      faultRosterRejected:
        'roster frame refused by this browser — the field is still showing the population it had',
      faultStatusRejected:
        'status frame refused by this browser — freshness below is the last one that passed',
    },
    compare: {
      title: 'Comparison',
      closeAria: 'Close comparison',
      status: 'STATUS',
      domains: 'DOMAINS',
      relatedResearch: 'RELATED RESEARCH',
    },
    common: {
      status: 'Status',
      domains: 'Domains',
      relatedResearch: 'Related research',
      projects: 'Projects',
      knowledgeConnections: 'Knowledge connections',
      allProjects: 'All projects',
      allResearch: 'All research',
      links: 'Links',
      summary: 'Summary',
      hypothesis: 'Hypothesis',
      problem: 'Problem',
      currentState: 'Current state',
      methods: 'Methods',
      findings: 'Findings',
      state: 'State',
      zone: 'Zone',
    },
  },
  ru: {
    nav: {
      command: 'Командный центр',
      anu: 'ANU Live',
      research: 'Исследования',
      projects: 'Проекты',
      agents: 'Агенты',
      knowledge: 'Знания',
      publications: 'Публикации',
      observatory: 'Обсерватория',
      about: 'О лаборатории',
      contact: 'Контакты',
    },
    header: {
      nodeOnline: 'УЗЕЛ В СЕТИ',
      ask: 'СПРОСИТЬ',
      askAria: 'Спросить лабораторию (открыть командную панель)',
      audioOnAria: 'Выключить фоновый звук',
      audioOffAria: 'Включить фоновый звук',
      openMenu: 'Открыть меню',
      close: 'ЗАКРЫТЬ',
      menuAria: 'Меню сайта',
      groupLab: 'Лаборатория',
      groupIndex: 'Указатель',
      homeAria: 'на главную',
      skip: 'Перейти к содержимому',
      langSwitchAria: 'Сменить язык',
    },
    footer: {
      tagline: 'Независимая AI-first R&D-лаборатория',
      liveNote: {
        LIVE: 'Фоновая активность агентов — реальная популяция NanoAgent, проверенная сайтом по цепочке.',
        STALE:
          'Фоновая активность агентов отражает последнюю проверенную по цепочке популяцию; живая вселенная сейчас без обновлений.',
        SYNCING:
          'Сайт ещё не установил, откуда берётся его фоновый слой агентов, — идёт подключение к наблюдателю.',
        OFFLINE:
          'Фоновая телеметрия агентов на этом сайте — обозначенная симуляция.',
      },
      canaryNote:
        'Наблюдаемый запуск — инженерная канарейка, а не флагманская вселенная: часть её решений может быть кодовым резервом движка, а не моделью.',
      sections: 'Разделы',
      elsewhere: 'Ссылки',
      footerAria: 'Подвал',
      rights: 'Независимая AI-first R&D-лаборатория',
    },
    consent: {
      text: 'Поведенческий слой сайта работает локально, в вашем браузере. Необязательная аналитика загружается только с вашего согласия.',
      privacyLink: 'Политика конфиденциальности',
      essential: 'Только необходимое',
      allow: 'Разрешить аналитику',
    },
    hint: {
      eyebrow: 'LAB · наблюдение',
      dismiss: 'Скрыть',
      knowledge: {
        text: 'Похоже, вам интересно, как знание движется по лаборатории.',
        action: 'Открыть систему знаний →',
      },
      knowledgeResearch: {
        text: 'Похоже, вам интересно, как знание движется по лаборатории.',
        action: 'Посмотреть исследования за этим →',
      },
      applied: {
        text: 'Ищете, что лаборатория умеет создавать?',
        action: 'Открыть прикладные исследования →',
      },
      publications: {
        text: 'Лаборатория держит свои утверждения проверяемыми.',
        action: 'Публикации и артефакты →',
      },
    },
    spotlight: {
      researcher:
        'Сигнал: исследовательский интерес · в приоритете: Исследования, Публикации',
      developer:
        'Сигнал: инженерный интерес · в приоритете: Агенты, Знания, GitHub',
      partner:
        'Сигнал: интерес к сотрудничеству · в приоритете: Проекты, Контакты',
      dismiss: 'Скрыть',
    },
    ask: {
      interface: 'ASK LAB · интерфейсный слой',
      palette: 'панель',
      terminal: 'терминал',
      esc: 'esc',
      placeholder: 'Спросите лабораторию или выберите команду…',
      thinking: 'LAB думает …',
      stop: 'esc — остановить',
      select: 'выбор',
      run: 'запуск',
      escClose: 'esc — закрыть',
      footnotePrefix:
        'Вопрос покидает браузер только через сервер лаборатории — к управляемому модельному сервису. Без профилирования и долгой истории. ',
      footnoteLink: 'Как это работает',
      quick: {
        research: 'Исследования',
        projects: 'Проекты',
        agents: 'Агенты',
        knowledge: 'Знания',
        observatory: 'Обсерватория',
        publications: 'Публикации',
        compare: 'Сравнить StudyNinja и KnowledgeBase AI',
        researching: 'Что вы исследуете?',
        graph: 'Показать граф знаний',
        terminal: 'терминал',
      },
      hint: {
        research: '/research',
        projects: '/projects',
        agents: '/agents',
        knowledge: '/knowledge',
        observatory: '/observatory',
        publications: '/publications',
        compare: 'сравнить',
        askLab: 'спросить LAB',
        graph: 'граф',
        mode: 'режим',
      },
      msg: {
        offline:
          'Шлюз ИИ сейчас недоступен. Терминал по-прежнему отвечает из реестров — введите «terminal».',
        rate: 'Достигнут лимит запросов. Шлюз принимает несколько вопросов в минуту — попробуйте чуть позже.',
        generic: 'Шлюз не ответил. Попробуйте ещё раз через мгновение.',
        tooLong:
          'Вопрос слишком длинный — уложитесь в 2000 символов и спросите снова.',
      },
      askThingLabel: 'Спросить лабораторию',
    },
    live: {
      label: 'ЛЕНТА LAB',
      awaiting: 'ОЖИДАНИЕ ТЕЛЕМЕТРИИ —',
      expandAria: 'Развернуть живую ленту лаборатории',
      collapseAria: 'Свернуть живую ленту лаборатории',
      real: 'реально',
      simulationLabel: 'ФОНОВАЯ СИМУЛЯЦИЯ',
      realEventsMarked: 'РЕАЛЬНЫЕ СОБЫТИЯ ПОМЕЧЕНЫ',
      unknownLabel: 'ИСТОЧНИК ЕЩЁ НЕ УСТАНОВЛЕН',
      canaryLabel: 'ИНЖЕНЕРНАЯ КАНАРЕЙКА',
      faultLabel: 'КАДР ОТКЛОНЁН',
      faultAria: 'Браузер отклонил кадр от моста лаборатории',
      faultRosterRejected:
        'кадр состава отклонён этим браузером — поле показывает прежнюю популяцию',
      faultStatusRejected:
        'кадр статуса отклонён этим браузером — ниже показана последняя прошедшая проверку свежесть',
    },
    compare: {
      title: 'Сравнение',
      closeAria: 'Закрыть сравнение',
      status: 'СТАТУС',
      domains: 'ОБЛАСТИ',
      relatedResearch: 'СВЯЗАННЫЕ ИССЛЕДОВАНИЯ',
    },
    common: {
      status: 'Статус',
      domains: 'Области',
      relatedResearch: 'Связанные исследования',
      projects: 'Проекты',
      knowledgeConnections: 'Связи знаний',
      allProjects: 'Все проекты',
      allResearch: 'Все исследования',
      links: 'Ссылки',
      summary: 'Обзор',
      hypothesis: 'Гипотеза',
      problem: 'Проблема',
      currentState: 'Текущее состояние',
      methods: 'Методы',
      findings: 'Результаты',
      state: 'Состояние',
      zone: 'Зона',
    },
  },
};

export function getDict(lang: Lang): Dict {
  return dictionaries[lang];
}
